import AddIcon from "@mui/icons-material/Add";
import {
  Autocomplete,
  Box,
  Button,
  CircularProgress,
  Grid,
  IconButton,
  InputAdornment,
  Tooltip,
  MenuItem,
  TextField,
  Typography,
} from "@mui/material";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs from "dayjs";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { useNavigate } from "react-router-dom";
import DeleteIcon from "@mui/icons-material/Delete";
import six_dot_icon from "../../../../../assets/AccountingModule/six_dot_icon.svg";
import React, { useEffect, useState } from "react";
import humbbleIcon from "../../../../../assets/AccountingModule/humbleicons_upload.svg";
import color from "../../../../../constants/color";
import styles from "./style";
import { QuoteValidation } from "../../../../../helper/Validation";

const EditQuote = ({
  customerList,
  itemList,
  loading,
  handleFileUpload,
  quoteData,
  updateQuote,
}) => {
  const [customer, setCustomer] = useState(null);
  const [selectedFile, setSelectedFile] = useState(null);
  const [filePreview, setFilePreview] = useState("");
  const navigate = useNavigate();
  useEffect(() => {
    if (quoteData) {
      setCustomer(quoteData.customer);
      if (quoteData.attachmentUrl) {
        setAttachmentUrl(quoteData.attachmentUrl);
      }
      setQuoteNumber(quoteData.quoteNumber || "");
      setReferenceNumber(quoteData.referenceNumber || "");
      setQuoteDate(quoteData.quoteDate ? dayjs(quoteData.quoteDate) : null);
      setExpiryDate(quoteData.expiryDate ? dayjs(quoteData.expiryDate) : null);
      setSalesperson(quoteData.salesperson || "");
      setSubject(quoteData.subject || "");
      setCustomerNotes(quoteData.customerNotes || "");
      setTermsAndConditions(quoteData.termsAndConditions || "");
      setAttachmentUrl(quoteData.attachmentUrl || "");
      setDiscountPercentage(quoteData.discountPercentage || 0);
      setTaxPercentage(quoteData.taxPercentage || 0);
      setCustomerId(quoteData.customer?._id || "");

      // Set items
      if (quoteData.items && quoteData.items.length > 0) {
        setItems(
          quoteData.items.map((item) => ({
            name: item.name || "",
            itemId: item.itemId || "",
            description: item.description || "",
            invoiceQuantity: item.quantity || 0,
            quantity: item.quantity || 0,
            rate: item.rate || 0,
            amount: item.amount || 0,
          }))
        );
      }
    }
  }, [quoteData]);

  const [quoteDate, setQuoteDate] = useState(null);
  const [expiryDate, setExpiryDate] = useState(null);
  const [items, setItems] = useState([
    {
      name: "",
      itemId: "",
      description: "",
      invoiceQuantity: "",
      rate: "",
      amount: "",
    },
  ]);

  const [customerId, setCustomerId] = useState("");
  const [quoteNumber, setQuoteNumber] = useState("");
  const [referenceNumber, setReferenceNumber] = useState("");
  const [subject, setSubject] = useState("");
  const [salesperson, setSalesperson] = useState("");
  const [customerNotes, setCustomerNotes] = useState("");
  const [termsAndConditions, setTermsAndConditions] = useState("");
  const [discountPercentage, setDiscountPercentage] = useState(0);
  const [taxPercentage, setTaxPercentage] = useState(0);
  const [attachmentUrl, setAttachmentUrl] = useState("");
  const [errors, setErrors] = useState({
    customer: "",
    quoteNumber: "",
    referenceNumber: "",
    quoteDate: "",
    expiryDate: "",
    salesperson: "",
    subject: "",
    items: [{
      item: "",
      description: "",
      quantity: "",
      rate: "",
    }],
    customerNotes: "",
    termsAndConditions: "",
    discount: "",
    tax: "",
  });

  const handleAddItem = () => {
    setItems([
      ...items,
      {
        name: "",
        itemId: "",
        description: "",
        quantity: "",
        rate: "",
        amount: "",
      },
    ]);

    setErrors(prev => ({
      ...prev,
      items: [
        ...prev.items,
        {
          item: "",
          description: "",
          quantity: "",
          rate: "",
        }
      ]
    }));
  };
  const validateField = (fieldName, value, index = null) => {
    const rule = QuoteValidation[fieldName];

    if (!rule) return ""; // No rule defined, consider valid

    // Check if required field is empty
    if (rule.required && !value) {
      return rule.validator.errEmpty;
    }

    // Skip validation for empty non-required fields
    if (!rule.required && !value) {
      return "";
    }

    // Special validation for dates
    if (fieldName === 'expiryDate' && quoteDate && value && value < quoteDate) {
      return "Expiry date must be after quote date";
    }

    // Validate against regex pattern
    if (value && !rule.validator.regEx.test(value)) {
      return rule.validator.error;
    }

    return ""; // No error
  };


  const handleRemoveItem = (index) => {
    if (items.length > 1) {
      const updatedItems = [...items];
      updatedItems.splice(index, 1);
      setItems(updatedItems);

      const updatedErrors = { ...errors };
      updatedErrors.items.splice(index, 1);
      setErrors(updatedErrors);
    }
  };

  const handleFormChange = (field, value) => {
    const error = validateField(field, value);

    setErrors(prev => ({
      ...prev,
      [field]: error
    }));

    // Update the corresponding state
    switch (field) {
      case 'quoteNumber':
        setQuoteNumber(value);
        break;
      case 'referenceNumber':
        setReferenceNumber(value);
        break;
      case 'subject':
        setSubject(value);
        break;
      case 'salesperson':
        setSalesperson(value);
        break;
      case 'customerNotes':
        setCustomerNotes(value);
        break;
      case 'termsAndConditions':
        setTermsAndConditions(value);
        break;
      case 'discount':
        setDiscountPercentage(Number(value) || 0);
        break;
      case 'tax':
        setTaxPercentage(Number(value) || 0);
        break;
      default:
        break;
    }
  };

  const handleItemChange = (value, index, key) => {
    const updatedItems = [...items];
    const updatedErrors = { ...errors };

    if ((key === "quantity" || key === "rate") && Number(value) < 0) {
      return;
    }

    if (key === "quantity" || key === "rate") {
      updatedItems[index][key] = Number(value);
    } else {
      updatedItems[index][key] = value;
    }

    // Validate the changed field
    if (key === "name") {
      const error = validateField("item", value);
      updatedErrors.items[index] = {
        ...updatedErrors.items[index],
        item: error
      };
    } else if (key === "description") {
      const error = validateField("description", value);
      updatedErrors.items[index] = {
        ...updatedErrors.items[index],
        description: error
      };
    } else if (key === "quantity") {
      const error = validateField("quantity", value);
      updatedErrors.items[index] = {
        ...updatedErrors.items[index],
        quantity: error
      };
    } else if (key === "rate") {
      const error = validateField("rate", value);
      updatedErrors.items[index] = {
        ...updatedErrors.items[index],
        rate: error
      };
    }

    // Recalculate amount if quantity or rate changes
    if (key === "quantity" || key === "invoiceQuantity" || key === "rate") {
      const qty = Number(updatedItems[index].invoiceQuantity) || 0;
      const rate = Number(updatedItems[index].rate) || 0;
      updatedItems[index].amount = qty * rate;
    }

    if (key === "name") {
      const selectedItem = itemList?.find((item) => item.title === value);
      if (selectedItem) {
        updatedItems[index].itemId = selectedItem._id || "";
        updatedItems[index].rate = Number(selectedItem.sellingPrice || 0);
        updatedItems[index].quantity = Number(selectedItem.quantity || 1);
        updatedItems[index].invoiceQuantity = 0;
        updatedItems[index].description = selectedItem.description || "";
        updatedItems[index].amount = "";

        // Validate the auto-filled fields
        const quantityError = validateField("quantity", updatedItems[index].quantity.toString());
        const rateError = validateField("rate", updatedItems[index].rate.toString());

        updatedErrors.items[index] = {
          ...updatedErrors.items[index],
          quantity: quantityError,
          rate: rateError
        };
      }
    }

    setItems(updatedItems);
    setErrors(updatedErrors);
  };

  const calculateSubTotal = () => {
    const total = items.reduce((sum, item) => {
      const amount =
        (Number(item.invoiceQuantity) || 0) * (Number(item.rate) || 0);
      return sum + amount;
    }, 0);
    return Number(total.toFixed(2)) || 0;
  };


  const calculateDiscountValue = () => {
    const subTotal = calculateSubTotal();
    const discount = (subTotal * (Number(discountPercentage) || 0)) / 100;
    return Number(discount.toFixed(2)) || 0;
  };

  const calculateTaxValue = () => {
    const subTotal = calculateSubTotal();
    const discountedAmount = subTotal - calculateDiscountValue();
    return discountedAmount * (taxPercentage / 100);
  };

  const calculateGrandTotal = () => {
    const subTotal = calculateSubTotal();
    const discountValue = calculateDiscountValue();
    const taxValue = calculateTaxValue();
    return subTotal - discountValue + taxValue;
  };

  const formatDisplayNumber = (num) => {
    if (!num) return "0";
    const number = Number(num);
    if (number >= 1000000000) {
      return (number / 1000000000).toFixed(1) + "B";
    } else if (number >= 1000000) {
      return (number / 1000000).toFixed(1) + "M";
    } else if (number >= 1000) {
      return (number / 1000).toFixed(1) + "K";
    }
    return number.toString();
  };

  const isFormValid = () => {
    if (
      !customerId ||
      !quoteNumber ||
      !quoteDate ||
      !expiryDate
    ) {
      return false;
    }

    const isItemsValid =
      items.length > 0 &&
      items.every(
        (item) =>
          item.name &&
          item.description &&
          item.invoiceQuantity &&
          item.rate &&
          item.amount
      );

    const isDiscountValid =
      discountPercentage !== null && discountPercentage >= 0;
    const isTaxValid = taxPercentage !== null && taxPercentage >= 0;

    return isItemsValid && isDiscountValid && isTaxValid;
  };

  const handleSubmit = () => {
    const subTotal = calculateSubTotal();
    const discountValue = calculateDiscountValue();
    const taxValue = calculateTaxValue();
    const grandTotal = calculateGrandTotal();

    const payload = {
      customer: customerId,
      quoteNumber,
      referenceNumber,
      quoteDate: quoteDate ? dayjs(quoteDate).format("YYYY-MM-DD") : null,
      expiryDate: expiryDate ? dayjs(expiryDate).format("YYYY-MM-DD") : null,
      salesperson,
      subject,
      items: items.map((item) => ({
        itemId: item.itemId,
        name: item.name,
        description: item.description,
        quantity: item.invoiceQuantity,
        rate: item.rate,
        amount: (item.rate || 0) * (item.invoiceQuantity || 0),
      })),
      customerNotes,
      total: subTotal,
      discountPercentage,
      discountValue,
      taxPercentage,
      taxValue,
      grandTotal,
      termsAndConditions,
      attachmentUrl: "https://yourdomain.com/uploads/quote-103.pdf",
    };

    updateQuote(payload);
  };

  const handleSaveDraft = () => handleSubmit("Draft");
  const handleSaveAndPublish = () => handleSubmit("Published");

  const textFieldStyles = {
    '& .MuiOutlinedInput-root': {
      borderRadius: '8px',
      '&.Mui-error fieldset': {
        borderColor: 'red',
        borderWidth: '2px',
      },
    },
  };

  return (
    <>
      <Box px="20px" mt={2}>
        <Box sx={styles.header}>
          <Typography sx={styles.title}>Edit Quote</Typography>
        </Box>
      </Box>
      <Box sx={styles.container}>
        {/* Form Section */}
        <Box sx={styles.formSection}>
          <Grid container spacing={2}>
            {/* First Row */}
            <Grid item xs={12} md={4}>
              <Box sx={styles.field}>
                <Typography sx={styles.label}>Customer Name</Typography>
                <Autocomplete
                  id="customer-select"
                  options={[
                    { _id: "add_new", name: "+ New Customer" },
                    ...(customerList || []),
                  ]}
                  getOptionLabel={(option) => option?.name || ""}
                  value={customer}
                  // popupIcon={<CustomDropdownIcon />}
                  onChange={(event, newValue) => {
                    if (newValue?._id === "add_new") {
                      navigate("/dashboard/addNewCustomer"); // redirect page
                      return;
                    }
                    setCustomer(newValue);
                    setCustomerId(newValue?._id || "");
                  }}
                  renderOption={(props, option) => (
                    <li
                      {...props}
                      style={
                        option._id === "add_new"
                          ? { color: color.secondary }
                          : {}
                      }
                    >
                      {option.name}
                    </li>
                  )}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      placeholder="Select Customer"
                      size="small"
                      sx={{ ...styles.textField, ...textFieldStyles }}
                      error={!!errors.customer}
                      helperText={errors.customer}
                      InputProps={{
                        ...params.InputProps,
                        endAdornment: (
                          <>
                            {loading ? (
                              <CircularProgress color="inherit" size={20} />
                            ) : null}
                            {params.InputProps.endAdornment}
                          </>
                        ),
                      }}
                    />
                  )}
                />
              </Box>
            </Grid>
            <Grid item xs={12} md={4}>
              <Box sx={styles.field}>
                <Typography sx={styles.label}>Quote#</Typography>
                <TextField
                  fullWidth
                  placeholder="Quote"
                  size="small"
                  value={quoteNumber || ""}
                  sx={{ ...styles.textField, ...textFieldStyles }}
                  onChange={(e) => handleFormChange('quoteNumber', e.target.value)}
                  error={!!errors.quoteNumber}
                  helperText={errors.quoteNumber}
                />
              </Box>
            </Grid>
            <Grid item xs={12} md={4}>
              <Box sx={styles.field}>
                <Typography sx={styles.label}>Reference#</Typography>
                <TextField
                  fullWidth
                  placeholder="Reference"
                  value={referenceNumber || ""}
                  onChange={(e) => handleFormChange('referenceNumber', e.target.value)}
                  size="small"
                  sx={{ ...styles.textField, ...textFieldStyles }}
                  error={!!errors.referenceNumber}
                  helperText={errors.referenceNumber}
                />
              </Box>
            </Grid>

            {/* Second Row */}

            <Grid item xs={12} md={4}>
              <Box sx={styles.field}>
                <Typography sx={styles.label}>Quote Date</Typography>
                <LocalizationProvider dateAdapter={AdapterDayjs}>
                  <Box sx={styles.datePickerOuter}>
                    <DatePicker
                      value={quoteDate}
                      onChange={(newValue) => {
                        setQuoteDate(newValue);
                        const error = validateField("quoteDate", newValue);
                        setErrors(prev => ({ ...prev, quoteDate: error }));

                        // Revalidate expiry date if quote date changes
                        if (expiryDate) {
                          const expiryError = validateField("expiryDate", expiryDate);
                          setErrors(prev => ({ ...prev, expiryDate: expiryError }));
                        }
                      }}
                      inputFormat="DD/MM/YYYY"
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          size="small"
                          fullWidth
                          sx={{
                            ...styles.datePicker,
                            "& .MuiInputAdornment-root": {
                              paddingLeft: "5px",
                            },
                          }}
                        />
                      )}
                    />
                  </Box>
                </LocalizationProvider>
              </Box>
            </Grid>
            <Grid item xs={12} md={4}>
              <Box sx={styles.field}>
                <Typography sx={styles.label}>Expiry Date</Typography>
                <LocalizationProvider dateAdapter={AdapterDayjs}>
                  <Box sx={styles.datePickerOuter}>
                    <DatePicker
                      value={expiryDate}
                      onChange={(newValue) => {
                        setExpiryDate(newValue);
                        const error = validateField("expiryDate", newValue);
                        setErrors(prev => ({ ...prev, expiryDate: error }));
                      }}
                      inputFormat="DD/MM/YYYY"
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          size="small"
                          fullWidth
                          sx={{
                            ...styles.datePicker,
                            ...textFieldStyles,
                            "& .MuiOutlinedInput-root": {
                              borderRadius: "8px !important",
                              overflow: "hidden",
                            },
                          }}
                          error={!!errors.expiryDate}
                          helperText={errors.expiryDate}
                        />
                      )}
                    />
                  </Box>
                </LocalizationProvider>
              </Box>
            </Grid>
            <Grid item xs={12} md={4}>
              <Box sx={styles.field}>
                <Typography sx={styles.label}>Salesperson</Typography>
                <TextField
                  select
                  fullWidth
                  size="small"
                  sx={{ ...styles.textField, ...textFieldStyles }}
                  value={salesperson}
                  onChange={(e) => handleFormChange('salesperson', e.target.value)}
                  SelectProps={{
                    renderValue: (selected) => {
                      if (!selected)
                        return (
                          <span style={{ color: "#999" }}>
                            Select Salesperson
                          </span>
                        );
                      return selected === "64f27b8e1234567890abcdef"
                        ? "John Doe"
                        : "Jane Smith";
                    },
                  }}
                >
                  <MenuItem value="64f27b8e1234567890abcdef">John Doe</MenuItem>
                  <MenuItem value="64f27b8e1234567890abcddf">
                    Jane Smith
                  </MenuItem>
                </TextField>
              </Box>
            </Grid>

            {/* Third Row */}
            <Grid item xs={12}>
              <Box sx={styles.field}>
                <Typography sx={styles.label}>Subject</Typography>
                <TextField
                  fullWidth
                  rows={2}
                  placeholder="Enter subject"
                  size="small"
                  value={subject || ""}
                  onChange={(e) => handleFormChange('subject', e.target.value)}
                  sx={{
                    ...styles.multilineTextField,
                    ...textFieldStyles,
                    "& .MuiOutlinedInput-root": {
                      height: "76px",
                      borderRadius: "8px !important",
                    },
                  }}
                  error={!!errors.subject}
                  helperText={errors.subject}
                />
              </Box>
            </Grid>
            <Grid item xs={12} md={12}>
              <Box sx={styles.customerNotes}>
                <Typography>Customer Notes</Typography>
                <TextField
                  rows={2}
                  fullWidth
                  placeholder="Customer Notes"
                  value={customerNotes || ""}
                  onChange={(e) => handleFormChange('customerNotes', e.target.value)}
                  sx={{
                    ...styles.multilineTextField,
                    ...textFieldStyles, mt: 1,
                    "& .MuiOutlinedInput-root": {
                      height: "76px",
                      borderRadius: "8px !important",
                    },
                  }}
                  error={!!errors.customerNotes}
                  helperText={errors.customerNotes}
                />
                {/* </Box> */}
              </Box>
            </Grid>
          </Grid>
        </Box>
      </Box>
      {/* Items Section */}
      <Box sx={styles.container}>
        <Box sx={styles.itemsSection}>
          {items.map((item, index) => {
            const stockQuantity =
              itemList.find((p) => p._id === item.itemId)?.quantity || 0;
            const itemErrors = errors.items[index] || {};
            return (
              <Box key={index} sx={styles.itemRow}>
                {/* <img src={six_dot_icon} alt="drag" style={styles.sixDotsIcon} /> */}
                <Autocomplete
                  id={`item-select-${index}`}
                  options={[
                    { _id: "add_new", title: "+ New Item" },
                    ...(itemList || []),
                  ]}
                  getOptionLabel={(option) => option?.title || ""}
                  value={itemList?.find((i) => i.title === item.name) || null}
                  onChange={(event, newValue) => {
                    if (newValue?._id === "add_new") {
                      navigate("/dashboard/addNewItem"); // redirect page
                      return;
                    }
                    if (!newValue) {
                      handleItemChange("", index, "name");
                      handleItemChange("", index, "invoiceQuantity");
                      return;
                    }
                    handleItemChange(newValue?.title || "", index, "name");
                  }}
                  renderOption={(props, option) => (
                    <li
                      {...props}
                      style={
                        option._id === "add_new"
                          ? { color: color.secondary }
                          : {}
                      }
                    >
                      {option.title}
                    </li>
                  )}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      placeholder="Select Item"
                      size="small"
                      sx={{
                        ...styles.itemField,
                        ...textFieldStyles,
                        flex: 1, // Stretch proportionally
                        minWidth: 0, // Prevent overflow
                        height: 43, // outer height of the text field
                        "& .MuiInputBase-root": {
                          height: "100%",
                          fontSize: "1rem",
                        },
                        "& .MuiInputBase-input": {
                          height: "100%",
                          padding: "12px 14px", // control vertical padding for input
                          boxSizing: "border-box",
                        },
                      }}
                      error={!!itemErrors.item}
                      helperText={itemErrors.item}
                    />
                  )}
                />
                <TextField
                  size="small"
                  placeholder="Description"
                  value={item.description}
                  onChange={(e) =>
                    handleItemChange(e.target.value, index, "description")
                  }
                  sx={{
                    ...styles.descriptionField,
                    overflow: "hidden", // hide scrollbars
                    display: "flex",
                    textarea: {
                      resize: "none", // disable manual resize
                      overflow: "hidden",
                    },
                    minWidth: "320px",
                    height: 43, // outer height of the text field
                    "& .MuiInputBase-root": {
                      height: "100%",
                      fontSize: "1rem",
                    },
                    "& .MuiInputBase-input": {
                      height: "100%",
                      padding: "12px 14px", // control vertical padding for input
                      boxSizing: "border-box",
                    },
                  }}
                  error={!!itemErrors.description}
                  helperText={itemErrors.description}
                />

                <Grid item xs={12} lg={2}>
                  <Box
                    display="flex"
                    alignItems="center"
                    sx={{ ...styles.customInput, width: "100%" }}
                  >
                    <TextField
                      value={item.invoiceQuantity}
                      type="number"
                      placeholder="Quantity"
                      sx={{ ...styles.quality, ...textFieldStyles }}
                      onChange={(e) => {
                        const value = parseFloat(e.target.value);
                        const maxQuantity = stockQuantity;
                        if (
                          !isNaN(value) &&
                          value >= 0 &&
                          value <= maxQuantity
                        ) {
                          handleItemChange(value, index, "invoiceQuantity");
                        } else if (e.target.value === "") {
                          handleItemChange("", index, "invoiceQuantity");
                        } else {
                          const validValue = value < 0 ? 0 : maxQuantity;
                          handleItemChange(
                            validValue,
                            index,
                            "invoiceQuantity"
                          );
                        }
                      }}
                      error={!!itemErrors.quantity}
                      helperText={itemErrors.quantity}
                      InputProps={{
                        endAdornment: (
                          <InputAdornment position="end" variant="filled">
                            <Box
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                bgcolor: "#DEDEDE",
                                height: 41,
                                p: 0,
                                ...styles.inputBgBox,
                                pl: 1,
                              }}
                            >
                              <Box
                                sx={{
                                  minWidth: 21,
                                  height: "100%",
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "end",
                                  fontWeight: "600",
                                  userSelect: "none",
                                  borderRadius: "8px",
                                }}
                              >
                                {stockQuantity}
                              </Box>
                              <Tooltip
                                arrow
                                placement="top"
                                title={
                                  <Box
                                    sx={{
                                      bgcolor: "white",
                                      p: 1,
                                      borderRadius: "6px",
                                    }}
                                  >
                                    <Typography
                                      sx={{
                                        fontSize: "10px",
                                        fontWeight: 600,
                                        color: "#373D3F",
                                        mb: 1,
                                      }}
                                    >
                                      Stock Details
                                    </Typography>
                                    <Typography
                                      sx={{
                                        fontSize: "10px",
                                        color: "#373D3F",
                                        fontWeight: 400,
                                      }}
                                    >
                                      Stock on Hand :{" "}
                                      {stockQuantity || "0"}
                                    </Typography>
                                  </Box>
                                }
                                componentsProps={{
                                  tooltip: {
                                    sx: {
                                      bgcolor: "white !important",
                                      p: 0,
                                      border: "1px solid #878787 !important",
                                      boxShadow: "none !important",
                                      "& .MuiTooltip-arrow": {
                                        color: "white !important",
                                        "&::before": {
                                          border: "1px solid #878787",
                                          backgroundColor: "white !important",
                                        },
                                      },
                                    },
                                  },
                                }}
                              >
                                <IconButton
                                  size="small"
                                  edge="end"
                                  sx={{
                                    color: "#878787",
                                  }}
                                >
                                  <InfoOutlinedIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            </Box>
                          </InputAdornment>
                        ),
                      }}
                    />
                  </Box>
                </Grid>

                <TextField
                  size="small"
                  type="number"
                  placeholder="Rate"
                  value={item.rate === "" ? "" : String(item.rate)}
                  onChange={(e) =>
                    handleItemChange(e.target.value, index, "rate")
                  }
                  sx={{
                    ...styles.rateField,
                    ...textFieldStyles,
                    flex: 1,
                    minWidth: 0,
                    height: 43,
                    "& .MuiInputBase-root": {
                      height: "100%",
                      fontSize: "1rem",
                    },
                  }}
                  error={!!itemErrors.rate}
                  helperText={itemErrors.rate}
                />
                <TextField
                  size="small"
                  placeholder="Amount"
                  value={item.amount}
                  InputProps={{
                    readOnly: true,
                  }}
                  sx={{
                    ...styles.amountField,
                    flex: 1, // Stretch proportionally
                    minWidth: 0, // Prevent overflow
                    height: 43, // outer height of the text field
                    "& .MuiInputBase-root": {
                      height: "100%",
                      fontSize: "1rem",
                    },
                    "& .MuiInputBase-input": {
                      height: "100%",
                      padding: "12px 14px", // control vertical padding for input
                      boxSizing: "border-box",
                    },
                  }}
                />

                {index === 0 ? (
                  <IconButton onClick={handleAddItem} sx={styles.addButton}>
                    <AddIcon />
                  </IconButton>
                ) : (
                  <IconButton
                    onClick={() => handleRemoveItem(index)}
                    sx={styles.deleteButton}
                  >
                    <DeleteIcon />
                  </IconButton>
                )}
              </Box>
            )
          })}
        </Box>
      </Box>

      {/* Summary Section */}
      <Grid container spacing={0}>
        <Grid item xs={12} md={7}>
          <Box sx={styles.container4}>
            <Box
              sx={{
                height: "140px",
                backgroundColor: "white",
                borderRadius: "8px",
              }}
            >
              <Typography
                sx={{
                  fontSize: "14px",
                  fontWeight: 500,
                  color: "#202020",
                  mb: 3,
                  display: "flex",
                  alignItems: "center",
                  "&::before": {
                    content: '""',
                    width: "3px",
                    height: "16px",
                    backgroundColor: "#28AA4A",
                    marginRight: "8px",
                    borderRadius: "2px",
                  },
                }}
              >
                Discount and Tax (%)
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <Box>
                    <Typography
                      sx={{ fontSize: "14px", color: "#202020", mb: 0.5 }}
                    >
                      Discount
                    </Typography>
                    <TextField
                      fullWidth
                      size="small"
                      type="number"
                      placeholder="Enter Discount"
                      value={discountPercentage || ""}
                      onChange={(e) =>
                        setDiscountPercentage(Number(e.target.value))
                      }
                      sx={{
                        backgroundColor: "white",
                        "& .MuiOutlinedInput-root": {
                          borderRadius: "8px !important",
                        },
                      }}
                    />
                  </Box>
                </Grid>
                <Grid item xs={6}>
                  <Box>
                    <Typography
                      sx={{ fontSize: "14px", color: "#202020", mb: 0.5 }}
                    >
                      Tax
                    </Typography>
                    <TextField
                      fullWidth
                      size="small"
                      type="number"
                      placeholder="Enter Tax"
                      value={taxPercentage || ""}
                      onChange={(e) => setTaxPercentage(Number(e.target.value))}
                      sx={{
                        backgroundColor: "white",
                        "& .MuiOutlinedInput-root": {
                          borderRadius: "8px !important",
                        },
                      }}
                    />
                  </Box>
                </Grid>
              </Grid>
            </Box>
          </Box>
        </Grid>
        <Grid item xs={12} md={5}>
          <Box sx={styles.container3}>
            <Box
              sx={{
                backgroundColor: "white",
                borderRadius: "8px",
                height: "140px",
              }}
            >
              <Box
                sx={{ display: "flex", justifyContent: "space-between", mb: 1 }}
              >
                <Typography sx={{ color: "#666" }}>Total</Typography>
                <Typography sx={{ fontWeight: 500 }}>
                  {calculateSubTotal()}
                </Typography>
              </Box>
              <Box
                sx={{ display: "flex", justifyContent: "space-between", mb: 1 }}
              >
                <Typography sx={{ color: "#666" }}>
                  Tax ({taxPercentage || 0}%)
                </Typography>
                <Typography sx={{ fontWeight: 500 }}>
                  ${calculateTaxValue().toFixed(2)}
                </Typography>
              </Box>
              <Box
                sx={{ display: "flex", justifyContent: "space-between", mb: 1 }}
              >
                <Typography sx={{ color: "#666" }}>
                  Discount ({discountPercentage || 0}%)
                </Typography>
                <Typography sx={{ fontWeight: 500 }}>
                  -${calculateDiscountValue().toFixed(2)}
                </Typography>
              </Box>
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  pt: 1,
                  borderTop: "1px solid #eee",
                }}
              >
                <Typography sx={{ fontWeight: 500 }}>Grand Total</Typography>
                <Typography sx={{ fontWeight: 500 }}>
                  ${calculateGrandTotal()}
                </Typography>
              </Box>
            </Box>
          </Box>
        </Grid>
      </Grid>

      {/* Attachment Section */}
      <Box sx={styles.container}>
        <Grid container spacing={2.5} sx={styles.attachmentSection}>
          {/* Terms & Conditions Section - Left Side */}
          <Grid item xs={12} md={6}>
            <Box sx={styles.textAreaBox}>
              <Typography sx={{ fontWeight: "bold", mb: 1 }}>
                Terms & Conditions
              </Typography>
              <TextField
                fullWidth
                placeholder="Enter terms and conditions"
                value={termsAndConditions || ""}
                onChange={(e) => setTermsAndConditions(e.target.value)}
                sx={{
                  "& .MuiOutlinedInput-root": {
                    backgroundColor: "white",
                    height: "76px",
                    borderRadius: "8px !important",
                  },
                }}
              />
            </Box>
          </Grid>

          {/* Attach File Section - Right Side */}
          <Grid item xs={12} md={6}>
            <Box sx={styles.fileBox}>
              <Typography sx={{ fontWeight: "bold", mb: 1 }}>
                Attach File to Quote
              </Typography>
              <Box
                sx={{
                  ...styles.fileDropArea,
                  border: "2px dashed #ccc",
                  borderRadius: "8px !important",
                  p: 2,
                  minHeight: "76px",
                  display: "flex",
                  alignItems: "center",
                  backgroundColor: "white",
                  cursor: "pointer",
                  "&:hover": {
                    borderColor: "#28AA4A",
                  },
                }}
              >
                <input
                  type="file"
                  id="quoteFileUpload"
                  style={{ display: "none" }}
                />
                <label
                  htmlFor="quoteFileUpload"
                  style={{
                    cursor: "pointer",
                    width: "100%",
                    height: "100%",
                    display: "flex",
                    alignItems: "center",
                  }}
                >
                  {filePreview ? (
                    <Box sx={{ width: "100%", textAlign: "center" }}>
                      {selectedFile?.type.startsWith("image/") ? (
                        <img
                          src={filePreview}
                          alt="Preview"
                          style={{
                            maxWidth: "100%",
                            maxHeight: "60px",
                            objectFit: "contain",
                          }}
                        />
                      ) : (
                        <Box sx={{ p: 1, bgcolor: "#f5f5f5", borderRadius: 1 }}>
                          <Typography>{selectedFile?.name}</Typography>
                        </Box>
                      )}
                    </Box>
                  ) : (
                    <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                      <Button
                        variant="contained"
                        component="span"
                        startIcon={
                          <Box
                            component="span"
                            sx={{ display: "flex", alignItems: "center" }}
                          >
                            <img
                              src={humbbleIcon}
                              alt="Upload"
                              style={{ width: "16px", height: "16px" }}
                            />
                          </Box>
                        }
                        sx={{
                          backgroundColor: "#28AA4A",
                          color: "white",
                          textTransform: "none",
                          height: "36px",
                          borderRadius: "4px",
                          "&:hover": {
                            backgroundColor: "#229A41",
                          },
                        }}
                      >
                        Choose File
                      </Button>
                      <Typography sx={{ color: "#666" }}>
                        or drag and drop file here
                      </Typography>
                    </Box>
                  )}
                </label>
              </Box>
            </Box>
          </Grid>
        </Grid>
      </Box>

      {/* Action Buttons */}
        <Box sx={styles.actionButtons}>
          <Button
            variant="contained"
            sx={styles.draftButton}
            onClick={handleSaveDraft}
            disabled={!isFormValid()}
          >
            Save
          </Button>
          {/* <Button
            variant="contained"
            sx={styles.saveButton}
            onClick={handleSaveAndPublish}
            disabled={!isFormValid()}
          >
            Save and Publish
          </Button> */}
          <Button
            variant="outlined"
            sx={styles.cancelButton}
            onClick={() => {
              navigate("/dashboard/quotes");
            }}
          >
            Cancel
          </Button>
        </Box>
    </>
  );
};

export default EditQuote;
